import Vue from 'vue'
import Vuex from 'vuex'
import VueResource from 'vue-resource'
import * as _ from 'lodash'

import Utils from '@/providers/Utils'
import Api from '@/providers/Api'
// NB: import the User class for the current app
import User from './User'
import WellnessPlan from '@/providers/WellnessPlan'

Vue.use(VueResource)
Vue.use(Vuex)

const loadPatientParticipant = (state, args) => {
    if (args.force || args.participantId != state.patientParticipantId) {
        return Api.getItem(null, 'participant', args.participantId,
            [
                'plan', 'plan.planResources', 'surveyNotes', 'providers', 'providers.practice',
                'wellness_plan', 'latestSurveys', 'latestScoreDefs', 'surveys',
                'latestBiometrics', 'latestAnthropometrics', 'metrics',
                'needs_check', 'rpm_checks', 'unbilled_checks', 'last_rpm_check_date'
            ])
            .then((participant) => {
                if (participant && participant.id) { // participant can be null if request is unauthorized
                    if (!participant.wellness_plan) {
                        participant.wellness_plan = WellnessPlan.defaultPlan
                    }
                    state.patientParticipant = participant
                    state.patientParticipantId = participant.id

                    // look up provider for current practitioner
                    state.patientParticipantProvider = _.find(participant.providers, (provider) => {
                        // NB: practitionerPracticeId() takes masking into account
                        return provider.practice_id == User.practitionerPracticeId()
                    })
                    if (state.patientParticipantProvider) {
                        state.patientParticipantProviderUsesDietId =
                            state.patientParticipantProvider.practice.use_dietid_questionnaire
                    }
                }
                return state.patientParticipant
            })
    } else {
        return state.patientParticipant
    }
}

const debouncedLoadPatientParticipant = _.debounce(loadPatientParticipant, 1000, {leading: true})

const savePatientParticipant = (state, args) => {
    state.saving = true
    console.log('store:savePatientParticipant')
    return Api.updateItem(this, 'participant', state.patientParticipant)
        .then(() => {
            state.saving = false
            loadPatientParticipant(state, {participantId: state.patientParticipantId, force: true})
        })
        .catch((response) => {
            state.saving = false
        })
}

const debouncedSavePatientParticipant = _.debounce(savePatientParticipant, 1000)

const saveSurveyNote = (state) => {
    console.log('store:saveSurveyNote')
    state.saving = true
    return new Promise((resolve, reject) => {
        return Api.updateItem(null, 'survey-note', state.draftSurveyNote, false)
            .then((response) => {
                state.saving = false
                resolve(response)
            })
            .catch((response) => {
                state.saving = false
                reject(response)
            })
    })
}

const debouncedSaveSurveyNote = _.debounce(saveSurveyNote, 1000)

// -----------------------------------------------------

export const store = new Vuex.Store({
    state: {
        // general state
        environment: null,
        language: 'en',
        theme: null, // this is set in App.vue/Theme.js
        translationsLoaded: false,
        currentUser: {},
        yii2MaskingUserStack: [], // last element is top of stack
        breadcrumbs: null,
        saving: false,

        // used by PractitionerPatientMixin
        patientParticipantId: null,
        patientParticipant: {},
        patientParticipantProvider: {},
        patientParticipantProviderUsesDietId: false,

        // used by ReportMixin
        participantScores: {}, // historical scores to show trends

        // used by SurveyForm
        survey: {},
        canEditSurvey: false,
        canCommentSurvey: false,

        // used by SurveyNote
        draftSurveyNote: {},

        // used by MyLogs
        dateRange: null,

        // sidebar panes
        helpPane: false,
        chatPane: false,
        videoPane: false,

        // video chat
        videoEnabled: false,
        videoState: null,
        mic: null,
        micDevice: null,
        camera: null,
        cameraDevice: null,
        //speaker: null,
        //speakerDevice: null,
    },
    mutations: {
        set (state, args) {
            // save value
            for (let property in args) {
                state[property] = args[property]
            }
        }
    },
    getters: {
        get: (state) => (field) => {
            return state[field]
        }
    },
    actions: {
        init (context) {
            if (localStorage.getItem('vuex')) {
                const savedState = JSON.parse(localStorage.getItem('vuex'))
                if (savedState)
                    _.each(savedState, (value, key) => {
                        // only persist some of the items, since restoring everything messes up the session
                        switch (key) {
                            case 'helpPane':
                            case 'chatPane':
                            case 'videoPane':
                            case 'videoEnabled':
                            case 'videoState':
                            case 'mic':
                            case 'micDevice':
                            case 'camera':
                            case 'cameraDevice':
                            //case 'speaker':
                            //case 'speakerDevice':
                                Vue.set(context.state, key, value)
                                break;
                        }
                    })
            }
        },
        save (context) {
          localStorage.setItem('vuex', JSON.stringify(context.state))
        },
        autosaveSurveyNote({commit, state}) {
            // when multiple input events (from various components; see SurveyNoteEdit::watch) trigger
            // an autosave in close proximity, ignore all but the LAST request
            return debouncedSaveSurveyNote(state)
        },
        getSurvey({commit, state}, args) {
            return new Promise((resolve, reject) => {
                Api.getItem(null, 'survey', args.surveyId, [
                    'participant', 'participant.plan', 'participant.latestScoreDefs',
                    'participant.latestBiometrics', 'participant.latestAnthropometrics',
                    'participant.plan.planResources', 'questionnaire'
                ])
                    .then((survey) => {
                        state.survey = survey
                        state.canEditSurvey = User.practitionerId() != null
                        resolve(survey)
                    })
            })
        },
        getParticipantScores({commit, state}, args) {
            Api.getParticipantScores(null, args.participantId)
                .then((items) => {
                    state.participantScores = items
                })
        },
        getPatientParticipant({commit, state}, args) {
            // when multiple components (using PatientPageMixin or PractitionerPatientMixin)
            // request a load in close proximity, ignore all but the FIRST request
            return debouncedLoadPatientParticipant(state, args)
        },
        savePatientParticipant({commit, state}, args) {
            debouncedSavePatientParticipant(state, args)
        },
    }
})
